import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	MutableRefObject,
	memo,
	ForwardRefRenderFunction,
	useImperativeHandle,
	forwardRef,
	Dispatch,
	SetStateAction,
	useTransition
} from "react";

import "./DetailsList.css";
import DetailsItem from "../details-item/DetailsItem";
import { Property, Building, Site, FeatureImage } from "../../../models/Property";
import { Link } from "react-router-dom";
import ContactForm from "../contact-form/ContactForm";
import { watch, when } from "@arcgis/core/core/reactiveUtils.js";
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import { intersect } from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";
import SearchFilter from "../search-filter/SearchFilter"
import Loading from "../../shared/loading/Loading";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { propertyNameFormatter, propertySaleStatus, propertySizeFormatter } from "../../../utilities/propertyUtilities";
import { MobileMainContentTypes } from "../mobile-view-nav";
import errorLogger from "../../../utilities/errorLogger";
import { useAuth } from "../../../context/useAuth";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import * as Utils from '../../../utilities/propertyUtilities';
import TextSymbol from "@arcgis/core/symbols/TextSymbol.js";
import FeatureReductionCluster from "@arcgis/core/layers/support/FeatureReductionCluster.js";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer.js";


const highlightLayerId: string = 'DetailsListHighlight';


type DetailsListHandle = {
	updateDetailsListCallback: () => void;
}
interface iProps {
	view: React.MutableRefObject<__esri.MapView>;
	setProperty: (property: Property | undefined) => void;
	filterByMapExtent: boolean;
	stateFilterGeometry: __esri.Polygon | null;
	radiusFilterGeometry: __esri.Polygon | null;
	filters?: React.RefObject<React.ElementRef<typeof SearchFilter>>;
	stateOptions: { value: string; label: string }[];
	setMobileMainContent: Dispatch<SetStateAction<MobileMainContentTypes>>;
};

const DetailsList: ForwardRefRenderFunction<DetailsListHandle, iProps> = ({
	view,
	setProperty,
	filterByMapExtent,
	stateFilterGeometry,
	filters,
	stateOptions,
	setMobileMainContent,
	radiusFilterGeometry
}: iProps, ref) => {
	useImperativeHandle(ref, () => ({ updateDetailsListCallback }));
	const { setAuthInitialized } = useAuth();
	const [isPending, startTransition] = useTransition();
	const [properties, setProperties] = useState<Property[] | undefined>();
	const allPropertiesDefinitionExpression = useRef<string>("");
	const [showContactUs, setShowContactUs] = useState(false);

	const layersInitialized = useRef(false);
	const propertiesLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
	const pointLayerRef: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();

	const filtersUpdateWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
	const viewUpdateWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
	const onClickWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();

	const addEventListenerAbort = useRef<AbortController>();
	const onHoverWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
	const onPopupCloseWatchHandle: MutableRefObject<__esri.WatchHandle | undefined> = useRef();
	const mouseOverDiv = useRef(false);
	const popupDiv = useRef<HTMLElement | null>(null);

	const viewExtent = useRef(view.current?.extent);

	const highlightLayer = useRef<__esri.GraphicsLayer>(
		new GraphicsLayer({
			id: highlightLayerId,
			listMode: 'hide'
		})
	);
	const highlightPointSymbol: MutableRefObject<__esri.SimpleMarkerSymbol | undefined> = useRef();
	const highlightPolygonSymbol: MutableRefObject<__esri.SimpleFillSymbol | undefined> = useRef();
	const abortController = useRef<AbortController>();
	const queryPropertyEntities = useCallback((where: string) => {
		let geometry: __esri.Geometry | null | undefined = undefined;

		if (filterByMapExtent) {
			geometry = viewExtent.current;
		}

		if (stateFilterGeometry) {
			if (geometry) {
				const intersectResult = intersect(geometry, stateFilterGeometry)
				if (intersectResult instanceof Geometry || intersectResult == null) {
					geometry = intersectResult;
				} else if (intersectResult.length > 0) {
					geometry = intersectResult[0];
				}
			} else {
				geometry = stateFilterGeometry;
			}
		}

		if (radiusFilterGeometry) {
			if (geometry) {
				const intersectResult = intersect(geometry, radiusFilterGeometry)
				if (intersectResult instanceof Geometry || intersectResult == null) {
					geometry = intersectResult
				} else if (intersectResult.length > 0) {
					geometry = intersectResult[0];
				}
			} else {
				geometry = radiusFilterGeometry;
			}
		}

		if (geometry === null) {
			setProperties([]);
			return;
		}

		const getProperties = async (where: string): Promise<Property[] | undefined> => {
			let featureSet: __esri.FeatureSet | undefined;

			let orderbyFromSearch = 'sitebuilding_name ASC';
			const { activeSearch, bSortBy, sSortBy } = filters?.current?.getFiltersSnapshot() || {};

			if ((activeSearch === 'Buildings' || activeSearch === 'Both') && bSortBy) {
				orderbyFromSearch = getOrderByFromSortBySelection(bSortBy)
			}
			else if (activeSearch === 'Sites' && sSortBy) {
				orderbyFromSearch = getOrderByFromSortBySelection(sSortBy)
			}

			try {
				// Query for features
				featureSet = await propertiesLayer.current?.queryFeatures({
					where: where,
					returnGeometry: true,
					outFields: ['*'],
					geometry: geometry ?? undefined,
					outSpatialReference: view.current.spatialReference,
					returnDistinctValues: true,
					orderByFields: [orderbyFromSearch],
				}, { signal: abortController.current?.signal });
			} catch (error) {
				if (!errorLogger(error)) {
					return; // Return an empty array on error
				}
			}

			let images: any;
			if (featureSet && featureSet?.features.length !== 0) {

				const objectIds = featureSet?.features.map(f => f.attributes.OBJECTID || f.attributes.objectid);
				const attachmentsQuery: __esri.AttachmentQueryProperties = {
					objectIds: objectIds,
					attachmentTypes: ["image/jpeg", "image/png"], // Query for image attachments
				};
				

				try {
					// Attempt to query attachments (may fail if the layer doesn't support them)
					images = await propertiesLayer.current?.queryAttachments(attachmentsQuery, { signal: abortController.current?.signal });
				  } catch (error) {
					// If the query fails, it's likely because the layer doesn't support attachments
					// console.warn('Error querying attachments:', error);
					images = []; // Set to an empty array to avoid further issues
				  }

			} else { return []; }
			
			const properties = featureSet?.features.reduce((acc: Property[], feature) => {
				return acc.concat(mapProperty(feature, images/*, boundaryMap*/));
			}, []);
			return properties;
		}

		getProperties(where)
			.then((properties) => {
				setProperties(properties);
			})
			.catch(errorLogger);

	}, [filterByMapExtent, filters, stateFilterGeometry, view, radiusFilterGeometry]);

	const getOrderByFromSortBySelection = (sortBy: string): string => {
		let orderByString;
		switch (sortBy) {
			case 'Name A-Z':
				orderByString = 'sitebuilding_name ASC';
				break;
			case 'Name Z-A':
				orderByString = 'sitebuilding_name DESC';
				break;
			case 'SqFt High':
				orderByString = 'building_area_sq_ft DESC';
				break;
			case 'SqFt Low':
				orderByString = 'building_area_sq_ft ASC';
				break;
			case 'Closest Interstate':
				orderByString = 'nearest_interstate_miles DESC';
				break;
			default:
				orderByString = 'sitebuilding_name ASC';
		}

		return orderByString;
	}

	const mapProperty = (feature: Graphic, attachments: Record<number, __esri.AttachmentInfo[]>): Property => {
		const atts = feature.attributes;
		let primaryImage = "";
		let thumbnailImage = ""
		let mappedImages: string[] = [];
		const id = atts.OBJECTID ?? atts.Control_ID;


		const featureImages = attachments[id];

		if (featureImages && featureImages.length > 0) {
			mappedImages = featureImages
				.map((img: FeatureImage) => {
					const tokenUrl: string = img.url;
					const imgName = img.name.toLowerCase();
		
					if (imgName.includes("thumbnail")) {
						thumbnailImage = tokenUrl;
						return null;
					} else if (imgName.includes("primary")) {
						primaryImage = tokenUrl;
					}
		
					return tokenUrl;
				})
				.filter(Boolean) as string[];
		
			if (!primaryImage) {
				primaryImage = mappedImages[0];
			}
			if (!thumbnailImage) {
				thumbnailImage = mappedImages[0];
			}
		}

		// Map base property, Building and Site
		const property = {
			_nearestAirport: atts.nearest_airport_miles,
			additionalAmenities: atts.additional_amenities,
			additionalAmenitiesOther: atts.additional_amenities_other,
			brochureUrl: atts.brochure_urls,
			brokerContact: atts.broker_contact,
			brokerContactPhone: atts.broker_contact_phone,
			brokerEmail: atts.broker_email,
			companyName: atts.company_name,
			floodPlainDesignation: atts.floodplain_designation,
			geometry: feature.geometry,
			highwayMiles: atts.nearest_highway_miles,
			households: atts.households,
			images: mappedImages,
			thumbnailImage: thumbnailImage,
			primaryImage: primaryImage,
			inFloodPlain: atts.is_sitebuilding_in_floodplain,
			isSiteOrBuilding: atts.is_this_a_site_or_building,
			medianSalary: atts.median_household_income,
			nearestAirport: atts.nearest_airport_name,
			nearestHighway: atts.nearest_highway_name,
			nearestInterstate: atts.nearest_interstate_name,
			nearestPortMil: atts.nearest_port_miles,
			nearestPortName: atts.nearest_port_name,
			numberOfCarSpots: atts.number_of_car_spots,
			OBJECTID: atts.OBJECTID ?? atts.objectid,
			otherWatcoContact: atts.watco_contact_copy_other,
			population: atts.population,
			siteBuildingAddress: atts.sitebuilding_address ?? atts.site_address,
			siteBuildingCity: atts.sitebuilding_city,
			siteBuildingDescription: atts.sitebuilding_description,
			siteBuildingName: atts.sitebuilding_name,
			siteBuildingState: atts.state,
			siteBuildingStatus: atts.sitebuilding_status,
			siteBuildingUtilities: atts.sitebuilding_utilities,
			siteBuildingZipCode: atts.site_zip_code,
			sourceGlobalId: atts.source_globalid,
			type: atts.is_this_a_site_or_building,
			watcoContact: atts.watco_contact,
			watcoContactEmail: atts.watco_contact_email?.toLowerCase(),
			watcoContactOther: atts.watco_contact_other,
			watcoContactPhone: atts.watco_contact_phone,
			workforce: atts.workforce,
			incentives: atts.incentives,
			transloading: atts.transloading,
		} as Property;

		const buildingArea = atts.building_area_sq_ft ?? (
			(atts.building_office_sq_ft != null || atts.building_warehouse_sq_ft != null)
				? atts.building_office_sq_ft + atts.building_warehouse_sq_ft
				: null
		);

		let building = {
			ltbRatio: atts.building_ltb_ratio,
			areaSqFt: buildingArea,
			buildingSaleOrLease: atts.building_for_sale_or_lease,
			indoorStorage: atts.building_indoor_storage,
			tenancy: atts.building_tenancy,
			buildingName: atts.building_name,
			numberOfDockDoors: atts.number_of_dock_doors,
			numberOfDriveInDoors: atts.number_of_drivein_doors,
			outdoorStorage: atts.building_outdoor_storage,
			buildingRailProvider: atts.building_rail_provider,
			buildingRailServed: atts.building_rail_served,
			specialtyFeatures: (atts.building_specialty_features as string)?.toLowerCase().split(','),
			specialtyFeaturesOther: (atts.building_specialty_features_o as string)?.toLowerCase().split(','),
			buildingType: atts.building_type,
			zoning: atts.building_zoning,
			zoningOther: atts.building_zoning_other,
			officeSqFt: atts.building_office_sq_ft,
			warehouseSqFt: atts.building_warehouse_sq_ft

		} as Building;

		let site = {
			address: atts.site_address,
			areaAcres: atts.site_area_acres,
			siteSaleOrLease: atts.site_for_sale_or_lease,
			//utilities vvv
			cabelInternet: atts.site_cableinternet_capacity,
			cabelInternetProvider: atts.site_cableinternet_provider,
			electricalProvider: atts.site_electric_provider,
			electricalCapcity: atts.site_electric_capacity,
			gasProvider: atts.site_gas_provider,
			gasCapacity: atts.site_gas_capacity,
			stormWaterProvider: atts.site_stormwater_provider,
			stormWaterCapacity: atts.site_stormwater_capacity,
			sewerProvider: atts.site_sewerwastewater_provider,
			sewerCapacity: atts.site_sewerwastewater_capacity,
			waterProvider: atts.site_water_provider,
			waterCapacity: atts.site_water_capacity,
			//utilities ^^^
			certified: atts.site_certified,
			county: atts.site_county,
			siteRailProvided: atts.site_rail_provider,
			siteRailServed: atts.site_rail_served,
			withinCity: atts.site_within_city,
			specialtyFeatures: (atts.site_specialty_features as string)?.split(','),
			specialtyFeaturesOther: (atts.site_specialty_features_other as string)?.split(','),
			zoning: atts.site_zoning,
			zoningOther: atts.site_zoning_other

		} as Site;

		// Add site and building to base property
		const buildingIsEmpty = Object.values(building).every(x => x === null || x === '' || x === undefined);
		const siteIsEmpty = Object.values(site).every(x => x === null || x === '' || x === undefined);

		if (!buildingIsEmpty) {
			property.building = building;
		}
		if (!siteIsEmpty) {
			property.site = site;
		}

		return property;
	}

	const updateDetailsListCallback = () => {
		if (allPropertiesDefinitionExpression.current === "" || !propertiesLayer.current) return;

		const propertiesLayerWhere = propertiesLayer.current.definitionExpression || "1=1";
		if (abortController.current) {
			abortController.current.abort();
		}
		abortController.current = new AbortController();
		startTransition(() => queryPropertyEntities(propertiesLayerWhere));
	}

	const updateDetailsList = useCallback(updateDetailsListCallback, [queryPropertyEntities]);

	
	const initLayers = () => {
		// Check if layers are already initialized
		if (!layersInitialized.current) {
		  layersInitialized.current = true;
	  
		  // Ensure that the view is fully initialized
		  view.current.when(() => {
			// Find the properties layer
			const layer = view.current.map.layers
			  .find(layer => layer.title === 'Properties') as __esri.FeatureLayer;
	  
			// Check if the layer is found and set it to propertiesLayer.current
			if (layer) {
			  propertiesLayer.current = layer;
	  
			  // Ensure the layer is loaded before accessing its fields
			  propertiesLayer.current.load().then(() => {
	  
				// Check if the layer has fields
				if (propertiesLayer.current?.fields) {
				  // Query features after the layer is loaded
				  propertiesLayer.current.queryFeatures({
					where: '1=1', // Simplified query; ensure this is valid
					returnGeometry: true,
					outFields: ['*']
				  }).then(featureSet => {
					if (featureSet.features.length > 0) {
					  const properties = featureSet.features
						.map(feature => {
						  const sitebuildingName = feature.attributes.sitebuilding_name;
	  
						  // Check if sitebuilding_name exists, is a string, and is not empty
						  if (sitebuildingName && typeof sitebuildingName === 'string' && sitebuildingName.trim() !== '') {
							return `'${sitebuildingName.trim()}'`;  // Call trim() safely
						  } else {
							return '';  // Skip this feature, return an empty string
						  }
						})
						.filter(property => property !== '')  // Remove empty entries
						.join(',');
	  
					  // Check if any valid properties are found
					  if (properties) {
						const definitionExpression = `sitebuilding_name IN (${properties})`;
						allPropertiesDefinitionExpression.current = definitionExpression;
					  } else {
						console.error('No valid properties found in featureSet.');
					  }
					} else {
					  console.error('No features found in featureSet.');
					}
	  
					// Watch for changes in the definitionExpression to update the list
					if (propertiesLayer.current) {
					  filtersUpdateWatchHandle.current = watch(
						() => propertiesLayer.current?.definitionExpression,
						updateDetailsList
					  );
					  updateDetailsList();
					}
	  
				  }).catch(error => {
					console.error("Error in queryFeatures:", error); // Log any errors during query
				  });
	  
				} else {
				  console.error('Fields are not available on propertiesLayer.current');
				}
			  }).catch(error => {
				console.error("Error loading properties layer:", error);
			  });
			} else {
			  console.error("Properties layer not found.");
			}
	  
			// Handle the highlight layer
			if (!view.current.map.findLayerById(highlightLayerId)) {
			  view.current.map.add(highlightLayer.current);
	  
			  const highlightOptions = view.current.highlightOptions;
			  const color = highlightOptions.color;
			  if (color) {
				const colorPalette = [color.r, color.g, color.b, highlightOptions.fillOpacity || 0.2];
				const symbol = new SimpleLineSymbol({
				  color: [color.r, color.g, color.b, highlightOptions.haloOpacity || 1],
				  width: 1,
				  style: 'solid'
				});
	  
				// Set Highlight for points
				highlightPointSymbol.current = new SimpleMarkerSymbol({
				  size: 20,
				  style: 'circle',
				  color: colorPalette,
				  outline: symbol
				});
	  
				// Set Highlights for polygons
				highlightPolygonSymbol.current = new SimpleFillSymbol({
				  color: colorPalette,
				  style: 'solid',
				  outline: symbol
				});
			  }
			}
	  
			// Monitor zoom level and toggle visibility of the propertiesLayer
			view.current.watch('zoom', (newZoomLevel) => {
			  if (newZoomLevel < 13) {
				// If zoom level is less than 13, hide the properties layer
				if (propertiesLayer.current) {
				  propertiesLayer.current.visible = false;
				}
			  } else {
				// If zoom level is 13 or greater, show the properties layer
				if (propertiesLayer.current) {
				  propertiesLayer.current.visible = true;
				}
			  }
			});
	  
			// Initial check of the zoom level and set visibility accordingly
			const initialZoomLevel = view.current.zoom;
			if (propertiesLayer.current) {
			  if (initialZoomLevel < 13) {
				propertiesLayer.current.visible = false;
			  } else {
				propertiesLayer.current.visible = true;
			  }
			}
		  });
		}
	  };
	  
	  // Using useEffect to call initLayers when dependencies change
	  useEffect(initLayers, [view, updateDetailsList, setAuthInitialized]);	  


	  const initPointLayerAndClustering = () => {
		if (propertiesLayer.current) {
		  propertiesLayer.current.load().then(() => {
			// Proceed only when the layer is loaded
			propertiesLayer.current?.queryFeatures({
			  where: "1=1", // Select all features
			  returnGeometry: true,
			  outFields: ['*'], // Ensure all fields are returned
			}).then((featureSet) => {
			    const pointFeatures = featureSet.features.map((polygonFeature) => {
				// Get the centroid of each polygon
				const centroid = (polygonFeature.geometry as __esri.Polygon).centroid;
				// Create a new point feature with the centroid's coordinates
				return new Graphic({
				  geometry: centroid,
				  attributes: polygonFeature.attributes
				});
			  });

			  // Create a new point layer with the centroid features
			  const pointLayer = new FeatureLayer({
				source: pointFeatures,  // Set the point features as the source
				title: 'Point Layer',
				geometryType: "point",
				spatialReference: { wkid: 4326 },
				objectIdField: "OBJECTID",
				fields: [
				  { name: "OBJECTID", type: "oid" }
				],
				renderer: new SimpleRenderer({
				  symbol: new SimpleMarkerSymbol({
					color: [238, 115, 38],  // Set the color for all points in the layer
					size: 7,
					outline: {
						color: [0, 0, 0], // Outline color (optional)
						width: 0.5        // Outline width (optional)
					  }
				  })
				})
			  });

			  // Store the pointLayer reference globally using useRef
			  pointLayerRef.current = pointLayer;
	  
			  // Create clustering configuration
  			    const clusteringConfig = new FeatureReductionCluster({
				clusterRadius: 37.5,  // Radius in pixels
				clusterMinSize: 16, // Min features per cluster
				clusterMaxSize: 37.5, // Max features per cluster
				maxScale: 40000, // Max zoom for clustering
				// Add labeling info for the cluster
				labelingInfo: [{
				  labelExpressionInfo: {
					expression: "Text($feature.cluster_count)"  // Use cluster_count to show number of items
				  },
				  symbol: new TextSymbol({
					color: [255, 255, 255], // White color for text
					font: { // Set font size,family, and weight
						size: 12, 
						family: 'Arial', 
						weight: "bold" 
					} 
				  }),
				  labelPlacement: "center-center", // Place the label at the center of the cluster
				  deconflictionStrategy: "none" // Avoid label overlap if any
				}]
			  });
	  
			  // Apply the feature reduction (clustering) to the point layer
			  pointLayer.featureReduction = clusteringConfig;
	  
			  // Add the point layer to the map
			  view.current.map.add(pointLayer);
	  
			  // Refresh the layer to apply the clustering
			  pointLayer.refresh();
	  
			  const toggleLayersVisibility = () => {
				const zoomLevel = view.current.zoom;
				// Define your threshold zoom level
				const thresholdZoom = 13; 
				// Check if the current zoom level is greater than the threshold
				zoomLevel < thresholdZoom ? pointLayer.visible = true : pointLayer.visible = false;
			  };
	  
			  // Initially check the zoom level when the page loads
			  toggleLayersVisibility();
	  
			  // Watch for scale (zoom) changes
			  view.current.watch("zoom", toggleLayersVisibility);
	  
			}).catch((error) => {
			  console.error("Error querying properties layer:", error);
			});
		  }).catch((error) => {
			console.error("Error loading properties layer:", error);
		  });
		}
	  };
	  
	  // Call initPointLayerAndClustering when the view or properties layer is ready
	  useEffect(initPointLayerAndClustering, [view, propertiesLayer.current]);
	  
	  
	  
	  
	const initExtentUpdateWatcher = () => {
		updateDetailsList();

		if (viewUpdateWatchHandle.current) {
			viewUpdateWatchHandle.current.remove();
		}
		if (filterByMapExtent) {
			viewUpdateWatchHandle.current = when(
				() => view.current.stationary,
				() => {
					if (viewExtent.current !== view.current.extent) {
						viewExtent.current = view.current.extent;
						updateDetailsList();
					}
				});
		}
	}
	useEffect(initExtentUpdateWatcher, [view, filterByMapExtent, updateDetailsList]);

	const initOnHoverWatcher = () => {
		if (onHoverWatchHandle.current) {
			onHoverWatchHandle.current.remove();
		}

		onHoverWatchHandle.current = view.current.on(
			"pointer-move",
			(evt: any) => {
				// Combine both layers into the hitTest query
				const hitTestLayer: (FeatureLayer)[] = [
					propertiesLayer.current,
					pointLayerRef.current
				].filter((layer): layer is FeatureLayer => layer !== undefined); // This ensures we're passing only valid layers
			
				return view.current.hitTest(evt, { include: hitTestLayer })
					.then(hit => {

						setTimeout(() => {
							const results = hit.results;

							if (results.length > 0 && results[0].type === 'graphic' && !mouseOverDiv.current) {
								const graphic = results[0].graphic;
								const hitTestId = graphic.attributes.OBJECTID;
								const property = properties?.find(p => p.OBJECTID === hitTestId);
								if (!property)
									return;

								let singleton = false;
								let poly = false;

								if (graphic.layer === pointLayerRef.current) {
									const clusterCount = graphic.attributes.cluster_count;
									if (clusterCount === 1 ) {
										singleton = true;
									}
								} else if (graphic.layer === propertiesLayer.current) {
									poly = true;
								}
								
								if (poly || singleton) {
									const popup = view.current.popup;
									popup.dockEnabled = false;
									popup.dockOptions = { buttonEnabled: false };

									// Used for rendering commponent as a string
									// for popup content.
									const div = document.createElement('div');
									const root = createRoot(div);

									flushSync(() => {
										root.render(<PopupContent property={property} />);
									});
									
									view.current?.openPopup({
										location: ((property.geometry as __esri.Polygon).centroid as __esri.Point),
										content: div,
									});
								
									if (addEventListenerAbort.current) {
										addEventListenerAbort.current.abort();
									}
									addEventListenerAbort.current = new AbortController();

									popupDiv.current = popup.container as HTMLElement;

									popupDiv.current?.addEventListener('mouseover', (e) => {
										if (mouseOverDiv.current !== true) {
											mouseOverDiv.current = true;
										}
									}, { signal: addEventListenerAbort.current?.signal });

									popupDiv.current?.addEventListener('mouseleave', (e) => {
										mouseOverDiv.current = false;
									}, { signal: addEventListenerAbort.current?.signal });

									if (onPopupCloseWatchHandle.current) {
										onPopupCloseWatchHandle.current.remove();
									}

									onPopupCloseWatchHandle.current = watch(
										() => popup.visible,
										() => {
											if (addEventListenerAbort.current) {
												addEventListenerAbort.current.abort();
											}

											mouseOverDiv.current = false;
											popupDiv.current = null;
										});
								}

							} else if (!mouseOverDiv.current) {

								if (addEventListenerAbort.current) {
									addEventListenerAbort.current.abort();
								}

								view.current.closePopup();
								popupDiv.current = null;
							}
						}, 100);
					});
			}
		);
	}
	useEffect(initOnHoverWatcher, [view, properties]);
	  
	  

	const initOnClickPropertyHandler = () => {
		if (onClickWatchHandle.current) {
		  onClickWatchHandle.current.remove();
		}
	  
		onClickWatchHandle.current = view.current.on(
		  "click",
		  (evt: any) => {
			// Combine both layers into the hitTest query
			const hitTestLayer: (FeatureLayer)[] = [
			  propertiesLayer.current,
			  pointLayerRef.current
			].filter((layer): layer is FeatureLayer => layer !== undefined); // This ensures we're passing only valid layers
	  
			view.current.hitTest(evt, { include: hitTestLayer })
			  .then(hit => {
				const results = hit.results;
	  
				if (results.length > 0) {
				  // Cast to GraphicHit (assuming it's always a graphic)
				  const graphicHit = results[0] as __esri.GraphicHit;
				  const graphic = graphicHit.graphic;
	  
				  // Check if it's a cluster (check cluster_count)
				  if (graphic.layer === pointLayerRef.current && graphic.attributes.cluster_count > 1) {
					// Zoom to the cluster's geometry (centroid)
					const clusterGeometry = graphic.geometry;
	  
					// Zoom in on the clicked cluster and center on its geometry
					view.current.goTo({
					  target: clusterGeometry,  // Set the geometry as the center for zooming
					  zoom: view.current.zoom + 5,  // Zoom in by 2 levels
					  duration: 1000
					});
				  } else if (graphic.layer === propertiesLayer.current || graphic.attributes.cluster_count === 1) {
					// For properties, just handle the property click as usual
					const hitID = graphic.attributes.OBJECTID;
					const property = properties?.find(p => p.OBJECTID === hitID);
					if (property) {
					  setProperty(property);
					}
				  }
				}
			  });
		  }
		);
	  };
	  
	  
	  useEffect(initOnClickPropertyHandler, [view, properties, setProperty]);

	const onContactUsClick = (publicName: string) => () => {
		// if (publicName) {
		//   setContactBusinessName(publicName);
		// } else {
		//   setContactBusinessName('');
		// }
		setShowContactUs(true);
	}

	const hideContactUs = () => {
		setShowContactUs(false);
	}

	const selectDetailItem = (entity: Property) => () => {
		startTransition(() => {
			highlightFeature(entity);
			setProperty(entity);
		});
	}

	const highlightFeature = (entity: Property) => () => {
		const zoomLevel = view.current.zoom;
		const zoomThreshold = 13;
	  
		// Remove any previous highlights from the highlightLayer
		highlightLayer.current?.removeAll();
	  
		// Determine the geometry and symbol based on the zoom level
		const isZoomedOut = zoomLevel < zoomThreshold;
		const geometry = isZoomedOut
		  ? (entity.geometry as __esri.Polygon).centroid as __esri.Point
		  : entity.geometry;
	  
		const symbol = isZoomedOut ? highlightPointSymbol.current : highlightPolygonSymbol.current;
	  
		// Create and add the graphic to the highlight layer
		const graphic = new Graphic({
		  geometry,
		  symbol,
		  attributes: entity.globalId,
		});
	  
		highlightLayer.current.add(graphic);
	  };
	  

	const removeFeatureHighlight = () => highlightLayer.current?.removeAll();

	const zoomToFeature = (entity: Property) => {
		startTransition(() => {
			view.current?.goTo(
				{
					target: entity.geometry,
					zoom: 14
				}, 
				{ 
					easing: 'ease-in', 
					duration: 5,
					signal: abortController.current?.signal 
				}
			)
			.then(_ => removeFeatureHighlight())
			.catch(errorLogger);

			setMobileMainContent(MobileMainContentTypes.MAP);
			setProperty(undefined);
		});
	}

	return (
		<div className="property-details-list-container">
			{(isPending || !properties) &&
				<div className="overlay">
					<div className="overlay-background">
						<Loading />
					</div>
				</div>
			}
			<div className="property-details-list">
				{properties?.length === 0 &&
					<div>
						<h4>No results found.</h4>
						<p>Is there something you're looking for but don't see it here? <Link to="/contact-us">Contact us.</Link></p>
					</div>
				}
				{showContactUs &&
					<div className="contact-us-form-container">
						<ContactForm onSubmitCallback={hideContactUs}
							stateOptions={stateOptions}
							filters={filters} />
						<div>
							<button onClick={hideContactUs}>Back to List</button>
						</div>
					</div>
				}
				{!showContactUs &&
					properties?.map((entity, index) =>
						<DetailsItem
							property={entity}
							key={index}
							selectHandler={selectDetailItem(entity)}
							zoomToHandler={() => zoomToFeature(entity)}
							onMouseEnter={highlightFeature(entity)}
							onMouseLeave={removeFeatureHighlight}
							onContactUsClick={onContactUsClick(entity.publicName!)} />
					)
				}
			</div>
			<div className="panel-header">
				<button
					className='bg-orange no-border reset-btn'
					onClick={filters?.current?.onReset}>
					<span className="esri-icon-undo"></span>
					<span>Reset</span>
				</button>
			</div>
		</div>
	)
}

export default memo(forwardRef(DetailsList));

interface PopupContentProps {
	property: Property;
}

function PopupContent({ property }: PopupContentProps) {
	const propertyName = propertyNameFormatter(property);
	const locationString = property.siteBuildingCity + ', ' + property.siteBuildingState;
	const propertySize = propertySizeFormatter(property, true);
	const saleOrLease = propertySaleStatus(property, true);

	return (
		// TODO - ASK SETH ABOUT THE ZOOM TO
		<div className='flex-column property-popup-item'>
			<div className="details">
				<div className="flex-row">
					<div className='public-name'>{propertyName}</div>
				</div>
				<div className="flex-row info--image__wrapper">
					<div className="thumbnail" style={{ backgroundImage: `url(${property.thumbnailImage})` }}>
					</div>
					<div className='flex-column details-info'>
						<div>{locationString}</div>
						<div>{propertySize}</div>
						<div>{saleOrLease}</div>
					</div>
				</div>
				<div className="property-popup__contact-info">
					<div>Contact Information</div>
					<div>{Utils.checkWatcoContact(property?.watcoContact, property?.watcoContactOther)}</div>
					<a href={`mailto:${property.watcoContactEmail}`}>{property?.watcoContactEmail}</a>
					{property.otherWatcoContact &&
						<>
							<div>{property?.otherWatcoContact}</div>
							<div>industrialdevelopment@watco.com</div>
							<a href={`mailto:industrialdevelopment@watco.com`}>industrialdevelopment@watco.com</a>
						</>
					}
				</div>
			</div>
		</div>
	);
}